import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Video, Trash2, Loader2, AlertCircle, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import HevcHelpDialog from "@/components/HevcHelpDialog";

/**
 * Phase 4 — Video uploader with XHR-based progress bar.
 *
 * Uses `POST /api/files/upload/video` (whitelisted MP4/WebM, size guarded
 * server-side). We ALSO fetch the live config once so the hint reflects
 * whatever `VIDEO_MAX_SIZE_MB` is set on the server without drift.
 *
 * Props:
 *  - value: string[]  storage paths of already-uploaded videos
 *  - onChange: (nextPaths, added?) => void
 *  - max: hard cap of videos (default 3)
 *  - testidPrefix: string
 */
export default function VideoUploader({ value = [], onChange, max = 3, testidPrefix = "video" }) {
  const { t } = useI18n();
  const [cfg, setCfg] = useState({ max_size_mb: 50, allowed_extensions: ["mp4", "webm"], allowed_content_types: ["video/mp4", "video/webm"] });
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showHevcHelp, setShowHevcHelp] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/files/video/config").then((r) => { if (alive) setCfg(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const doUpload = async (file) => {
    if (!file) return;
    setError("");
    const nameLower = (file.name || "").toLowerCase();
    const ext = nameLower.includes(".") ? nameLower.split(".").pop() : "";
    if (!cfg.allowed_extensions.includes(ext)) {
      setError(t("video_upload_err_format"));
      toast.error(t("video_upload_err_format"));
      return;
    }
    // Tolerant client-side MIME check — iOS share sheets sometimes drop or
    // generalise the Content-Type, so we accept anything that starts with
    // "video/" or is empty/octet-stream. The backend does the definitive
    // check + codec probe.
    const ct = (file.type || "").toLowerCase();
    const mimeOk = !ct || ct === "application/octet-stream" || ct.startsWith("video/");
    if (!mimeOk) {
      setError(t("video_upload_err_format"));
      toast.error(t("video_upload_err_format"));
      return;
    }
    const maxBytes = (cfg.max_size_mb || 50) * 1024 * 1024;
    if (file.size > maxBytes) {
      const m = t("video_upload_err_too_large").replace("{mb}", cfg.max_size_mb);
      setError(m);
      toast.error(m);
      return;
    }

    setBusy(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("tc_token") || "";
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = `${backend}/api/files/upload/video`;

      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch (err) { reject(err); }
          } else {
            let detail = "generic";
            try { detail = JSON.parse(xhr.responseText).detail || ""; } catch {}
            reject(new Error(detail || `HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error("network"));
        xhr.send(form);
      });

      const next = [...value, data.path];
      onChange(next, data);
      toast.success(t("video_upload_ok"));
    } catch (e) {
      const msg = String(e?.message || "generic");
      let localized = t("video_upload_err_generic");
      let hevc = false;
      if (msg.includes("video_hevc_not_supported")) { localized = t("video_upload_err_hevc"); hevc = true; }
      else if (msg.includes("video_format_unsupported")) localized = t("video_upload_err_format");
      else if (msg.includes("video_too_large")) localized = t("video_upload_err_too_large").replace("{mb}", cfg.max_size_mb);
      setError(localized);
      toast.error(localized);
      // Automatically surface the tiny iPhone-help card the first time a
      // user hits the HEVC block. Nobody else sees it.
      if (hevc) setShowHevcHelp(true);
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    if (!window.confirm(t("video_delete_confirm"))) return;
    const next = value.filter((_, i) => i !== idx);
    onChange(next, null);
    toast.success(t("video_removed"));
  };

  const canAddMore = value.length < max;

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-uploader`}>
      {/* Existing videos preview */}
      {value.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`${testidPrefix}-list`}>
          {value.map((path, i) => (
            <div key={path} className="relative rounded-xl overflow-hidden border border-slate-200 bg-black">
              <video
                src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${path}`}
                controls
                preload="metadata"
                playsInline
                className="w-full h-40 object-contain bg-black"
                data-testid={`${testidPrefix}-preview-${i}`}
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`${testidPrefix}-remove-${i}`}
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-700"
                title={t("video_delete")}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add-video control */}
      {canAddMore && (
        <div>
          <Button
            type="button"
            variant="outline"
            data-testid={`${testidPrefix}-add-btn`}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="rounded-full"
          >
            {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Video size={13} className="mr-1" />}
            {busy ? t("video_uploading") : t("video_upload_label")}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/x-m4v,.mp4,.webm,.mov,.m4v"
            className="hidden"
            data-testid={`${testidPrefix}-input`}
            onChange={(e) => doUpload(e.target.files?.[0])}
            disabled={busy}
          />
          <div className="text-[10px] text-slate-500 mt-1" data-testid={`${testidPrefix}-hint`}>
            {t("video_upload_hint").replace("{mb}", cfg.max_size_mb)}
          </div>
        </div>
      )}
      {busy && (
        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-testid={`${testidPrefix}-progress-bar`}>
          <div
            className="bg-emerald-600 h-2 transition-all"
            style={{ width: `${progress}%` }}
            data-testid={`${testidPrefix}-progress-fill`}
          />
        </div>
      )}
      {error && !busy && (
        <div className="text-xs text-red-600 flex items-center gap-1 flex-wrap" data-testid={`${testidPrefix}-err`}>
          <AlertCircle size={12} /> {error}
          {/^.*HEVC/i.test(error) || error === t("video_upload_err_hevc") ? (
            <button
              type="button"
              onClick={() => setShowHevcHelp(true)}
              className="ml-1 underline text-emerald-700 inline-flex items-center gap-0.5"
              data-testid={`${testidPrefix}-hevc-help-open`}
            >
              <HelpCircle size={11} /> {t("hevc_help_show")}
            </button>
          ) : null}
        </div>
      )}
      <HevcHelpDialog open={showHevcHelp} onOpenChange={setShowHevcHelp} />
    </div>
  );
}
